'use client';

import { CONSTANT } from '@const';

import { Directions, Mail, Phone } from '../icons';
import { Container, NavLink } from '../ui';

type NavigationItem = {
	name: string;
	href: string;
};

/** TODO: Confirm and add correct navigation items and links */
const navigation: NavigationItem[] = [
	{ name: 'Copyright', href: CONSTANT.QLD_GOV_COPYRIGHT_URL },
	{ name: 'Privacy', href: CONSTANT.QLD_GOV_PRIVACY_URL },
	{ name: 'Right to Information', href: CONSTANT.QLD_GOV_RIGHT_TO_INFORMATION_URL },
	{ name: 'Accessibility', href: CONSTANT.QLD_GOV_ACCESSIBILITY_URL },
	{ name: 'Other languages', href: CONSTANT.QLD_GOV_LANGUAGES_URL },
];

const labels = {
	title: 'Contact us',
	contactDescription: 'Get in touch for enquiries, feedback, complaints and compliments.',
	phone: 'Phone',
	email: 'Email',
	location: 'Location',
	contactOptions: 'Other contact options',
	contactOptionsMobile: 'Contact options',
	copyright: `The State of Queensland 1995-${new Date().getFullYear()}`,
	organisation: 'Queensland Government',
} as const;

export const Footer = () => {
	const ContactSection = () => {
		return (
			<div className="flex flex-col gap-6 sm:justify-between md:col-span-2 md:flex-col md:justify-start">
				<div className="flex shrink flex-col gap-6 sm:flex-row md:flex-col md:justify-between">
					<div className="flex shrink flex-col gap-6">
						<h4 className="text-neutral-white">{labels.title}</h4>
						<small className="text-neutral-white flex-wrap break-words">
							{labels.contactDescription}
						</small>
						<div className="flex flex-col gap-4">
							<NavLink
								theme="dark"
								href={`tel:${CONSTANT.WATERIQ_PHONE_NUMBER}`}
								leftIcon={<Phone className="size-icon-xs text-dark-action-secondary shrink-0" />}
								className="no-underline"
								onClick={e => {
									e.preventDefault();
									window.location.href = `tel:${CONSTANT.WATERIQ_PHONE_NUMBER}`;
								}}
							>
								<span className="text-xs break-words whitespace-normal">
									<b>{labels.phone}:</b> {CONSTANT.WATERIQ_PHONE_NUMBER}
								</span>
							</NavLink>
							<NavLink
								theme="dark"
								href={`mailto:${CONSTANT.WATERIQ_EMAIL_ADDRESS}`}
								leftIcon={<Mail className="size-icon-xs text-dark-action-secondary shrink-0" />}
								className="no-underline"
							>
								<span className="text-xs break-all whitespace-pre-line">
									<b>{labels.email}:</b> {CONSTANT.WATERIQ_EMAIL_ADDRESS}
								</span>
							</NavLink>
							<NavLink
								theme="dark"
								href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(CONSTANT.WATERIQ_LOCATION)}`}
								leftIcon={
									<Directions className="size-icon-xs text-dark-action-secondary shrink-0" />
								}
								className="no-underline"
							>
								<span className="text-xs break-words whitespace-normal">
									<b>{labels.location}:</b> {CONSTANT.WATERIQ_LOCATION}
								</span>
							</NavLink>
						</div>
					</div>
				</div>
			</div>
		);
	};

	const NavigationLinks = () => {
		const firstCol = navigation.slice(0, 3); // Copyright, Privacy, Right to Information
		const secondCol = navigation.slice(3); // Accessibility, Other languages

		return (
			<div className="flex w-full lg:justify-end">
				<div className="flex w-full flex-row gap-8 sm:gap-12 md:flex-col md:gap-2">
					{/* First column */}
					<ul role="list" className="flex flex-col gap-y-2">
						{firstCol.map(item => (
							<li key={item.name}>
								<NavLink
									theme="dark"
									href={item.href}
									className="text-xs break-words whitespace-normal"
								>
									{item.name}
								</NavLink>
							</li>
						))}
					</ul>
					{/* Second column */}
					<ul role="list" className="flex flex-col gap-y-2">
						{secondCol.map(item => (
							<li key={item.name}>
								<NavLink
									theme="dark"
									href={item.href}
									className="text-xs break-words whitespace-normal"
								>
									{item.name}
								</NavLink>
							</li>
						))}
					</ul>
				</div>
			</div>
		);
	};

	const CopyrightSection = () => (
		<div className="flex flex-col space-y-4 px-4 sm:px-6 lg:px-0">
			<small className="text-neutral-white">&copy; {labels.copyright}</small>
			<NavLink theme="dark" href={CONSTANT.QLD_GOV_URL} className="text-xs">
				{labels.organisation}
			</NavLink>
		</div>
	);

	return (
		<footer className="bg-dark-background border-light-design-accent text-neutral-white mt-auto h-fit border-t-4">
			<Container className="grid grid-cols-1 gap-8 !px-0 py-8 md:py-12 lg:grid-cols-[1fr_auto_1fr]">
				<div className="grid grid-cols-1 gap-8 px-4 sm:px-6 md:grid-cols-3 md:gap-12 lg:px-8">
					<ContactSection />
					<div className="bg-dark-border-alt block h-px w-full md:hidden" />
					<NavigationLinks />
				</div>
				<div className="bg-dark-border-alt lg:bg-dark-border block h-px w-full lg:h-full lg:w-[2px]" />
				<CopyrightSection />
			</Container>
		</footer>
	);
};
